import React, { useEffect, useRef } from "react";
import L from "leaflet";

const RISK_COLORS = { Low: "#10B981", Medium: "#F59E0B", High: "#F97316", Critical: "#EF4444" };

export const TurkeyMap = ({ points = [], captures = [], height = 380 }) => {
  const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
  const ref = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);

  useEffect(() => {
    if (!ref.current || mapRef.current) return;
    const map = L.map(ref.current, { attributionControl: true, zoomControl: true, scrollWheelZoom: false })
      .setView([39.0, 35.2], 5);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap contributors", maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;
    layerRef.current = L.layerGroup().addTo(map);
    setTimeout(() => map.invalidateSize(), 200);
    return () => { map.remove(); mapRef.current = null; };
  }, []);

  useEffect(() => {
    const map = mapRef.current, group = layerRef.current;
    if (!map || !group) return;
    group.clearLayers();
    const valid = points.filter((p) => p.lat && p.lng);
    valid.forEach((p) => {
      const color = RISK_COLORS[p.risk_level] || "#00FFFF";
      const icon = L.divIcon({
        className: "",
        html: `<div class="qs-pin" style="color:${color};background:${color}"></div>`,
        iconSize: [18, 18], iconAnchor: [9, 9],
      });
      L.marker([p.lat, p.lng], { icon }).addTo(group)
        .bindPopup(
          `<div style="font-family:monospace;color:#0A1128;min-width:150px">
            <strong>${esc(p.project || p.city)}</strong><br/>
            ${esc(p.city || "")}<br/>
            Risk: <b style="color:${color}">${esc(p.risk_level)}</b> (${esc(p.risk_score)})<br/>
            ${p.compliant ? "✔ TBDY 2018" : "✖ Non-compliant"}
          </div>`
        );
    });
    const caps = captures.filter((c) => c.lat && c.lng);
    caps.forEach((c) => {
      const icon = L.divIcon({ className: "", html: `<div class="qs-capture-pin" title="${esc(c.kind)}"></div>`, iconSize: [14, 14], iconAnchor: [7, 7] });
      const img = c.photo_src ? `<img src="${esc(c.photo_src)}" alt="" style="width:160px;height:100px;object-fit:cover;display:block;margin-bottom:4px"/>` : "";
      L.marker([c.lat, c.lng], { icon }).addTo(group)
        .bindPopup(`<div style="font-family:monospace;color:#0A1128;min-width:160px">${img}<strong>Field ${esc(c.kind)}</strong><br/>${esc(c.note || "—")}<br/><span style="color:#64748b">${esc((c.captured_at || "").slice(0, 16))}${c.accuracy_m ? ` · ±${Math.round(c.accuracy_m)} m` : ""}</span></div>`);
    });
    const all = [...valid, ...caps];
    if (all.length) {
      const bounds = L.latLngBounds(all.map((p) => [p.lat, p.lng]));
      map.fitBounds(bounds.pad(0.4), { maxZoom: all.length === valid.length ? 8 : 14 });
    }
  }, [points, captures]);

  return <div ref={ref} data-testid="turkey-map" style={{ height, width: "100%" }} className="border border-white/10" />;
};
